/**
 * LA PALETTE DE RECHERCHE GLOBALE (⌘K) — la pilule de la topbar (maquette) et son panneau.
 *
 * CE QU'ELLE CHERCHE, et RIEN d'autre (le placeholder le promet, §23.0) : les ARTICLES du magasin
 * ouvert (nom, code article, code-barres — la même recherche serveur que l'écran Articles), les
 * CLIENTS du fichier (nom, carte, téléphone, email — la même recherche serveur que l'écran
 * Fichier clients) et les ÉCRANS du back-office (par leur nom de rail).
 *
 * ⚠️ UN ARTICLE CHOISI OUVRE SA FICHE : la palette navigue vers /articles en portant la ligne déjà
 * chargée (état de navigation) — l'écran l'ouvre sans re-chercher. Un CLIENT choisi ouvre SA FICHE
 * de la même façon (/clients + identifiant en état de navigation). Un écran choisi navigue.
 * ⚠️ Le filtre de droits des écrans est CELUI du rail (même expression) : deux logiques de visibilité
 * finiraient par montrer ici ce que le rail cache (Forme A).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Trans } from '@orgazone/ui-web';
import { api, type ArticleModel, type ClientRow } from './api';
import { useAuth } from './auth';
import { NAV, OFF_MENU, RESTRICTED, type NavLeaf } from './nav';
import { key, t } from './i18n';

const MONO = 'var(--font-mono)';

/** Le raccourci affiché suit la machine : ⌘K sur Mac, Ctrl K ailleurs — on n'affiche pas un ⌘ à un PC. */
const MAC = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);
const RACCOURCI = MAC ? '⌘K' : key('Ctrl K');

/** Le rendu d'un mot mis en avant dans une phrase traduite — voir le `<Trans>` de l'aide. */
const MOT_FORT = (mot: string) => <strong style={{ color: 'var(--ink-700)' }}>{mot}</strong>;

interface Ecran { path: string; label: string; groupe: string | null }

/** Les écrans navigables — les feuilles du rail, aplaties avec leur groupe, droits appliqués. */
function ecransVisibles(permissions: string[]): Ecran[] {
  const out: Ecran[] = [];
  for (const e of NAV) {
    if (e.kind === 'leaf') out.push({ path: e.path, label: t(e.label), groupe: null });
    else for (const c of e.children) out.push({ path: c.path, label: t(c.label), groupe: t(e.label) });
  }
  // Le MÊME filtre que le rail (rail.tsx) — jamais une seconde logique de visibilité.
  for (const r of RESTRICTED.filter((x: NavLeaf) => !x.permission || permissions.includes(x.permission))) {
    out.push({ path: r.path, label: t(r.label), groupe: null });
  }
  // Les écrans sans entrée de menu se cherchent aussi : « Mon compte » se trouve en tapant son nom.
  for (const r of OFF_MENU) out.push({ path: r.path, label: t(r.label), groupe: null });
  return out;
}

type Resultat =
  | { kind: 'ecran'; ecran: Ecran }
  | { kind: 'article'; article: ArticleModel }
  | { kind: 'client'; client: ClientRow };

export function PaletteRecherche() {
  const [ouverte, setOuverte] = useState(false);

  // ⚠️ Le raccourci vit au NIVEAU FENÊTRE : la palette s'appelle depuis n'importe quel écran.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOuverte((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/* La pilule de la maquette — un BOUTON qui ouvre la palette, avec le raccourci affiché. */}
      <button
        type="button" onClick={() => setOuverte(true)}
        aria-label={t('Rechercher — articles, clients et écrans')}
        style={{
          display: 'flex', alignItems: 'center', gap: 9, height: 38, padding: '0 14px',
          borderRadius: 'var(--r-pill)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
          minWidth: 0, flex: '0 1 260px', color: 'var(--ink-400)', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
        </svg>
        <span style={{ flex: 1, minWidth: 0, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          
          {t('Rechercher un article, un client, un écran…')}
        </span>
        <span style={{ fontFamily: MONO, fontSize: '10.5px', padding: '2px 6px', borderRadius: 'var(--r-xs)', background: 'var(--paper)', border: '1px solid var(--ink-200)', whiteSpace: 'nowrap' }}>{t(RACCOURCI)}</span>
      </button>
      {ouverte && <PanneauPalette onClose={() => setOuverte(false)} />}
    </>
  );
}

function PanneauPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [articles, setArticles] = useState<ArticleModel[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [echecClients, setEchecClients] = useState<string | null>(null);
  const [curseur, setCurseur] = useState(0);
  const champRef = useRef<HTMLInputElement>(null);

  useEffect(() => { champRef.current?.focus(); }, []);

  const ecrans = useMemo(() => ecransVisibles(user?.permissions ?? []), [user]);
  const terme = q.trim().toLowerCase();
  // ⚠️ Mémoïsé : ce tableau nourrit le useMemo de `resultats` — recréé à chaque rendu, il
  //    annulerait la mémoïsation en aval (react-hooks/exhaustive-deps, CI --max-warnings 0).
  const ecransTrouves = useMemo(() => terme
    ? ecrans.filter((e) => e.label.toLowerCase().includes(terme) || (e.groupe ?? '').toLowerCase().includes(terme)).slice(0, 6)
    : [], [terme, ecrans]);

  // ⚠️ La recherche serveur est DÉBOUNCÉE (250 ms) et versionnée : la réponse d'une frappe dépassée
  //    ne doit jamais recouvrir celle de la frappe courante. Articles et clients partent ENSEMBLE
  //    mais échouent SÉPARÉMENT — une panne d'un côté ne vide pas l'autre.
  useEffect(() => {
    if (!terme) { setArticles([]); setClients([]); setLoading(false); setFailure(null); setEchecClients(null); return; }
    let vivant = true;
    setLoading(true);
    const t = window.setTimeout(() => {
      const a = api.articles({ page: 1, size: 8, sort: 'designation', dir: 'asc', q: terme })
        .then((r) => { if (vivant) { setArticles(r.rows ?? []); setFailure(null); } })
        .catch((e: unknown) => { if (vivant) { setArticles([]); setFailure(e instanceof Error ? e.message : String(e)); } });
      const c = api.clients({ q: terme, chip: '', sort: 'name', dir: 'asc', limit: 6, offset: 0 })
        .then((r) => { if (vivant) { setClients(r.rows ?? []); setEchecClients(null); } })
        .catch((e: unknown) => { if (vivant) { setClients([]); setEchecClients(e instanceof Error ? e.message : String(e)); } });
      void Promise.allSettled([a, c]).then(() => { if (vivant) setLoading(false); });
    }, 250);
    return () => { vivant = false; window.clearTimeout(t); };
  }, [terme]);

  const resultats: Resultat[] = useMemo(() => [
    ...ecransTrouves.map((ecran): Resultat => ({ kind: 'ecran', ecran })),
    ...articles.map((article): Resultat => ({ kind: 'article', article })),
    ...clients.map((client): Resultat => ({ kind: 'client', client })),
  ], [ecransTrouves, articles, clients]);

  useEffect(() => { setCurseur(0); }, [terme, resultats.length]);

  const activer = useCallback((r: Resultat) => {
    onClose();
    if (r.kind === 'ecran') { navigate(r.ecran.path); return; }
    if (r.kind === 'client') {
      // Un client choisi OUVRE SA FICHE : /clients reçoit l'identifiant en état de navigation
      // (consommé une fois) et ouvre la modale directement — plus de détour par la recherche.
      navigate('/clients', { state: { ficheDepuisPalette: r.client.id } });
      return;
    }
    // La ligne DÉJÀ chargée voyage avec la navigation : la fiche s'ouvre sans re-chercher.
    navigate('/articles', { state: { ficheDepuisPalette: r.article } });
  }, [navigate, onClose]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCurseur((c) => (resultats.length ? (c + 1) % resultats.length : 0)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCurseur((c) => (resultats.length ? (c - 1 + resultats.length) % resultats.length : 0)); return; }
    if (e.key === 'Enter' && resultats[curseur]) { e.preventDefault(); activer(resultats[curseur]!); }
  };

  const eyebrow = { padding: '8px 14px 4px', fontSize: '10px', fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase' as const, color: 'var(--ink-400)' };
  let indexGlobal = -1;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900 }} role="dialog" aria-label={t('Recherche globale')}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.45)', backdropFilter: 'blur(2px)' }} aria-hidden />
      <div style={{
        position: 'absolute', top: '12vh', left: '50%', transform: 'translateX(-50%)',
        width: 580, maxWidth: 'calc(100vw - 32px)', background: 'var(--paper)',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-lg)', overflow: 'hidden',
        boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--ink-100)' }}>
          <Icon name="search" size={17} color="var(--ink-400)" />
          <input
            ref={champRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder={t('Rechercher un article, un client (nom, carte…) ou un écran…')}
            aria-label={t('Rechercher un article, un client ou un écran')}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: '15px', color: 'var(--ink-900)' }}
          />
          <span style={{ fontFamily: MONO, fontSize: '10.5px', padding: '2px 6px', borderRadius: 'var(--r-xs)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{t('Échap')}</span>
        </div>

        <div style={{ maxHeight: '52vh', overflowY: 'auto', padding: '4px 0 8px' }}>
          {!terme && (
            <div style={{ padding: '18px 16px', fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.6 }}>
              {/* ⚠️ UNE SEULE CLÉ POUR TOUTE LA PHRASE. Écrite en quatre nœuds JSX autour de ses trois
                  mots en gras, elle produisait quatre clés — « Tapez pour chercher — les », « du
                  magasin ouvert… » — dont aucune n'est traduisible : le traducteur reçoit des bouts
                  sans la phrase, et l'ordre des mots change d'une langue à l'autre. Les repères
                  numérotés se DÉPLACENT avec le mot qu'ils entourent. */}
              <Trans
                k="Tapez pour chercher — les <1>articles</1> du magasin ouvert (nom, code article, code-barres), les <2>clients</2> du fichier (nom, carte, téléphone, email) et les <3>écrans</3> du back-office."
                parts={[MOT_FORT, MOT_FORT, MOT_FORT]}
              />
            </div>
          )}

          {terme && ecransTrouves.length > 0 && (
            <>
              <div style={eyebrow}>{t('Écrans')}</div>
              {ecransTrouves.map((e) => {
                indexGlobal += 1;
                const i = indexGlobal;
                return (
                  <RangDePalette key={e.path} actif={curseur === i} onSurvol={() => setCurseur(i)}
                    onActive={() => activer({ kind: 'ecran', ecran: e })}>
                    <span style={{ display: 'flex', color: 'var(--ink-400)', flexShrink: 0 }}><Icon name="chevronR" size={13} /></span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.label}</span>
                    {e.groupe && <span style={{ fontSize: '11px', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{e.groupe}</span>}
                  </RangDePalette>
                );
              })}
            </>
          )}

          {terme && (
            <>
              <div style={eyebrow}>{t('Articles')}</div>
              {loading && <div style={{ padding: '10px 16px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Recherche…')}</div>}
              {/* ⚠️ L'échec s'affiche avec son motif réel — un panneau muet ressemblerait à « aucun résultat ». */}
              {!loading && failure && (
                <div role="alert" style={{ padding: '10px 16px', fontSize: '12.5px', color: 'var(--danger-ink)' }}>{t('La recherche a échoué : {motif}', { motif: failure })}</div>
              )}
              {!loading && !failure && articles.length === 0 && (
                <div style={{ padding: '10px 16px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucun article ne correspond à « {terme} ».', { terme: q.trim() })}</div>
              )}
              {!loading && articles.map((a) => {
                indexGlobal += 1;
                const i = indexGlobal;
                const meta = [a.articleCode, a.gencode ?? t('{n} déclinaison', { n: a.variantCount }), a.family]
                  .filter(Boolean).join(' · ');
                return (
                  <RangDePalette key={a.modelKey} actif={curseur === i} onSurvol={() => setCurseur(i)}
                    onActive={() => activer({ kind: 'article', article: a })}>
                    <span style={{ display: 'flex', color: 'var(--ink-400)', flexShrink: 0 }}><Icon name="tag" size={14} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</span>
                      <span className="mono" style={{ display: 'block', fontSize: '10.5px', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta}</span>
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t('Fiche article')}</span>
                  </RangDePalette>
                );
              })}
            </>
          )}

          {terme && (
            <>
              <div style={eyebrow}>{t('Clients')}</div>
              {loading && <div style={{ padding: '10px 16px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Recherche…')}</div>}
              {!loading && echecClients && (
                <div role="alert" style={{ padding: '10px 16px', fontSize: '12.5px', color: 'var(--danger-ink)' }}>{t('La recherche a échoué : {motif}', { motif: echecClients })}</div>
              )}
              {!loading && !echecClients && clients.length === 0 && (
                <div style={{ padding: '10px 16px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucun client ne correspond à « {terme} ».', { terme: q.trim() })}</div>
              )}
              {!loading && clients.map((c) => {
                indexGlobal += 1;
                const i = indexGlobal;
                const nom = c.erased ? t('Client anonymisé') : `${c.lastName} ${c.firstName ?? ''}`.trim();
                const meta = [c.loyaltyNumber, c.phone, c.email].filter(Boolean).join(' · ');
                return (
                  <RangDePalette key={c.id} actif={curseur === i} onSurvol={() => setCurseur(i)}
                    onActive={() => activer({ kind: 'client', client: c })}>
                    <span style={{ display: 'flex', color: 'var(--ink-400)', flexShrink: 0 }}><Icon name="user" size={14} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: c.erased ? 'var(--ink-400)' : 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nom}</span>
                      {meta && <span className="mono" style={{ display: 'block', fontSize: '10.5px', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta}</span>}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t('Fichier clients')}</span>
                  </RangDePalette>
                );
              })}
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 14, padding: '9px 16px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-100)', fontSize: '11px', color: 'var(--ink-400)' }}>
          <span>{t('↑↓ naviguer')}</span><span>{t('Entrée ouvrir')}</span><span>{t('Échap fermer')}</span>
        </div>
      </div>
    </div>
  );
}

/** Un rang de palette — survol et curseur clavier partagent le même état, un seul rang est marqué. */
function RangDePalette({ actif, onSurvol, onActive, children }: {
  actif: boolean; onSurvol: () => void; onActive: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button" onClick={onActive} onMouseEnter={onSurvol}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '9px 16px',
        border: 'none', background: actif ? 'var(--ink-100)' : 'transparent', textAlign: 'left', cursor: 'pointer',
      }}
    >{children}</button>
  );
}
